import React from 'react';
import {
  Lang,
  CATEGORY_TYPES,
  TAGS_LAB_CONTENT_TYPE,
  EVENTS,
  ANALYTICS_CONTENT_CATEGORIES_TYPES,
  ENTITY_TYPES,
} from 'shared/constants';
import { Modal, TextInput } from 'src/components/design-system';
import { Radio } from 'antd';
import styles from './lab-category-editor.module.scss';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { TagsSelector, TAG_SELECTOR_TYPE } from 'src/components/tags/tagSelector';
import { DrugClassSelector } from 'src/components/drugClass/drugClassSelector';
import { HorizontalDirectoryCompendiumIcon, HorizontalCompendiumIcon } from 'src/images';
import analyticsService from 'src/helpers/analytics.service';
import { getCategoryType } from 'shared/utils';
import { MultipleOwnersSelector } from 'src/components/owners/owners-selector/multiple-owners-selector';
import { isSection } from 'shared/store/selectors/lab.selector';

const ALLOWED_CATEGORIES = {
  [CATEGORY_TYPES.CATEGORY]: { label: 'Category' },
  [CATEGORY_TYPES.VALUES]: {
    label: 'Values',
  },
};

const LAB_CATEGORY_SCHEMA = Yup.object().shape({
  title: Yup.string().min(2, 'Too Short!').max(200, 'Too Long!').required('Required!'),
  type: Yup.string().required(),
  ownerIds: Yup.array()
    .of(Yup.string())
    .min(1, 'Please assign at least one owner')
    .required('Required!'),
  specialty: Yup.string().nullable(),
  subspecialty: Yup.string().nullable(),
  contentType: Yup.string().nullable(),
  targetAudience: Yup.array().of(Yup.string()),
  drugClass: Yup.string().nullable(),
});

const LabCategoryEditorComponent = React.memo(function LabCategoryEditor({
  open = false,
  lang = Lang.LAB_CATEGORY_EDITOR,
  entity,
  onFinish = () => {},
  createCategory = () => {},
  updateCategory = () => {},
}) {
  entity = entity ?? { path: null, isSectionCreation: false };
  let isNew = !entity.id;
  let { isSectionCreation, path } = entity;
  const isCategorySection = isSectionCreation || isSection(path?.split(','));
  let title = isNew ? lang.ADD_TITLE : lang.EDIT_TITLE;

  if (isCategorySection) {
    title = isNew ? lang.ADD_SECTION_TITLE : lang.EDIT_SECTION_TITLE;
  }

  const [isLoading, setLoadingState] = React.useState(false);
  const [error, setError] = React.useState(null);

  const createOrUpdateCategoryAction = React.useCallback(
    async values => {
      setLoadingState(true);

      // TODO: create the category
      let categoryRes;
      const { specialty, subspecialty, contentType, targetAudience, ...valuesData } = values;

      const data = {
        ...valuesData,
        tags: { specialty, subspecialty, contentType, targetAudience },
        drugClass: contentType === TAGS_LAB_CONTENT_TYPE.DRUG_DOSAGE ? values.drugClass : null,
      };

      if (isNew) {
        categoryRes = await createCategory(data);

        if (categoryRes.error) {
          setError(categoryRes.error);
          setLoadingState(false);
          return;
        }
        analyticsService.track(EVENTS.CONTENT.UPLOAD, {
          entityType: ENTITY_TYPES.compendium,
        });
        if (categoryRes) {
          analyticsService.track(EVENTS.CATEGORIES.ADD, {
            categoryType: getCategoryType({
              path: categoryRes.path,
              type: categoryRes.type,
              isSectionCreation,
              isNew: true,
            }),
            contentType: ANALYTICS_CONTENT_CATEGORIES_TYPES.LAB,
            categoryId: categoryRes.id,
            title: categoryRes.title,
            ownerIds: categoryRes.ownerIds,
          });
        }
      } else {
        categoryRes = await updateCategory(data);

        if (categoryRes?.error) {
          setError(categoryRes.error);
          setLoadingState(false);
          return;
        }
        analyticsService.track(EVENTS.CONTENT.UPDATE, {
          entityType: ENTITY_TYPES.compendium,
        });
        analyticsService.track(EVENTS.CATEGORIES.EDIT, {
          categoryType: getCategoryType({ path: values.path, type: values.type }),
          contentType: ANALYTICS_CONTENT_CATEGORIES_TYPES.LAB,
          categoryId: values.id,
          title: values.title,
          ownerIds: values.ownerIds,
        });
      }

      onFinish({ ...categoryRes, isSectionCreation, isNew });
      setLoadingState(false);
    },
    [createCategory, updateCategory, isNew, isSectionCreation, onFinish],
  );

  const dismiss = React.useCallback(() => {
    // TODO: analytics

    onFinish({});
  }, [onFinish]);

  const { handleChange, handleSubmit, setFieldValue, values, errors, dirty, isValid } = useFormik({
    enableReinitialize: true,
    validationSchema: LAB_CATEGORY_SCHEMA,
    initialValues: {
      type: isSectionCreation ? CATEGORY_TYPES.CATEGORY : CATEGORY_TYPES.VALUES,
      ...entity.tags,
      ...entity,
      ownerIds: isNew ? [] : entity.ownerIds || [],
    },
    onSubmit: createOrUpdateCategoryAction,
  });

  const selectCategoryType = React.useCallback(
    e => {
      const value = e.target.value;
      setFieldValue('type', value);
    },
    [setFieldValue],
  );

  const resetField = React.useCallback(
    field => {
      // Clear the dependent field through Formik. resetForm() repointed initialValues at the
      // (mutated) current values, which dropped `dirty` and — with enableReinitialize — made the
      // next effect snap the whole form back to the entity, reverting the value the user had just
      // cleared. That made the specialty clear-X a no-op. See BB-17.
      setFieldValue(field, null);
    },
    [setFieldValue],
  );

  const renderIcon = key => {
    if (key === CATEGORY_TYPES.CATEGORY) {
      return <img src={HorizontalDirectoryCompendiumIcon} className={styles.icon} alt={key} />;
    }

    return <img src={HorizontalCompendiumIcon} alt={key} className={styles.icon} />;
  };

  const renderCategoryTypes = () => {
    if (!isNew || !path || isSectionCreation) return null;

    return (
      <>
        <label>{lang.SELECT_CATEGORY_TYPE}</label>
        <Radio.Group
          onChange={selectCategoryType}
          value={values.type}
          className={styles.categoriesTypes}
          buttonStyle="solid">
          {Object.keys(ALLOWED_CATEGORIES).map(key => (
            <Radio.Button key={key} value={key} className={styles.categorySelect}>
              {renderIcon(key)}
              {ALLOWED_CATEGORIES[key].label}
            </Radio.Button>
          ))}
        </Radio.Group>
      </>
    );
  };

  return (
    <Modal
      title={title}
      open={open}
      onCancel={dismiss}
      onSubmit={handleSubmit}
      cancelText={lang.DISMISS}
      submitText={isNew ? lang.CREATE : lang.UPDATE}
      loading={isLoading}
      submitDisabled={!isValid || !dirty || isLoading}
      width={600}>
      <form onSubmit={handleSubmit} className={styles.formContent}>
        {renderCategoryTypes()}
        <TextInput
          name="title"
          label={lang.INSERT_CATEGORY_NAME[values.type]}
          value={values.title}
          error={errors.title}
          onChange={handleChange}
          placeholder={lang.INSERT_CATEGORY_NAME_PLACEHOLDER[values.type]}
          required
        />

        <MultipleOwnersSelector
          label={lang.OWNER}
          size="large"
          value={values.ownerIds}
          error={errors.ownerIds}
          placeholder={lang.OWNER_PLACEHOLDER}
          name="ownerIds"
          onChange={setFieldValue}
          borderRadius={4}
          parentOwnerIds={entity?.ownerIds}
        />

        <TagsSelector
          selectedValues={values}
          errors={errors}
          onChange={setFieldValue}
          resetField={resetField}
          type={TAG_SELECTOR_TYPE.LAB}
        />

        {values.contentType === TAGS_LAB_CONTENT_TYPE.DRUG_DOSAGE && (
          <>
            <DrugClassSelector
              label={lang.DRUG_CLASS}
              value={values.drugClass}
              error={errors.drugClass}
              placeholder={lang.DRUG_CLASS_PLACEHOLDER}
              name="drugClass"
              onChange={setFieldValue}
            />
          </>
        )}

        {error && <p className={styles.error}>{error}</p>}
        {!isNew && <span className={styles.itemId}>Id: {entity?.id}</span>}
      </form>
    </Modal>
  );
});

export { LabCategoryEditorComponent };
